import { Component, EventEmitter, Output } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { StorageService } from 'src/app/common/services/storage/storage.service';
import { StatusEnum } from 'src/app/common/utils/status-enum.models';
import { ButtonColorEnum } from 'src/app/shared/buttons/enums/button-color.enum';
import { InputTypeEnum } from 'src/app/shared/input/enums/input-type.enum';
import { CategoryService } from '../../category/service/category.service';
import {
  GetCategoryRequest,
  GetCategoryResponseItem,
} from '../../category/service/models/category.service.model';
import {
  GetClientRequest,
  GetClientsResponse,
} from '../../client/models/client.model';
import { ClientService } from '../../client/service/client.service';
import {
  GetUserRequest,
  GetUserResponse,
} from '../../user/service/models/user.service.model';
import { UserService } from '../../user/service/user.service';
import { IncidentStatusEnum } from '../enums/incident-status.enum';
import { GetTranscriptionsFilter } from '../service/models/transcription.service.model';

@Component({
  selector: 'app-transcriptions-filter',
  templateUrl: './transcriptions-filter.component.html',
  styleUrls: ['./transcriptions-filter.component.scss'],
})
export class TranscriptionsFilterComponent {
  @Output() filterEmmiter: EventEmitter<GetTranscriptionsFilter> =
    new EventEmitter<GetTranscriptionsFilter>();

  form = this.formBuilder.group({
    protocolNumber: '',
    attendants: [['']],
    categories: [['']],
    clients: [['']],
    status: [['']],
    initialDate: [''],
    finalDate: [''],
    types: [['']],
  });

  isOpen: boolean = false;
  categories: string[] = [];
  attendants: string[] = [];
  clients: string[] = [];
  selectedCategories: string[] = [];
  status: string[] = [
    'Aguardando Análise',
    'Aguardando Informações',
    'Finalizado',
    'Erro',
  ];
  selectedStatus: string[] = [];
  isLoading: boolean = false;
  types: string[] = ['Áudio', 'Chat'];
  selectedTypes: string[] = [];

  loadingCategories: boolean = false;
  originalCategories: GetCategoryResponseItem[] = [];
  loadingMoreCategories: boolean = false;
  loadingSearchCategories: boolean = false;
  totalItemsCategories: number = 0;
  categoryFilter = {
    pageNumber: 1,
    pageSize: 10,
    status: [StatusEnum.ATIVO],
    sortAscending: true,
    sortBy: 'Name',
  } as GetCategoryRequest;

  ButtonColorEnum = ButtonColorEnum;
  InputTypeEnum = InputTypeEnum;

  loadingAttendants: boolean = false;
  loadingSearchAttendants: boolean = false;
  loadingMoreAttendants: boolean = false;
  selectedAttendants: string[] = [];
  originalAttendants: GetUserResponse[] = [];

  loadingClients: boolean = false;
  loadingSearchClients: boolean = false;
  loadingMoreClients: boolean = false;
  selectedClients: string[] = [];
  originalClients: GetClientsResponse[] = [];

  totalItemsAttendants: number = 0;
  totalItemsClients: number = 0;

  attendantsFilter = {
    pageSize: 10,
    pageNumber: 1,
    profiles: ['ATENDENTE'],
    status: [StatusEnum.ATIVO],
    sortAscending: true,
    sortBy: 'Name',
  } as GetUserRequest;

  clientsFilter = {
    pageSize: 10,
    pageNumber: 1,
    status: [StatusEnum.ATIVO],
    sortAscending: true,
    sortBy: 'Name',
  } as GetClientRequest;

  constructor(
    private readonly categoryService: CategoryService,
    private readonly formBuilder: FormBuilder,
    private readonly storageService: StorageService,
    private readonly userService: UserService,
    private readonly toastrService: ToastrService,
    private readonly clientService: ClientService
  ) {}

  ngOnInit() {
    this.getCategories(null);
    this.getAttendants(null);
    this.getClients(null);

    let strFilter = this.storageService.getCurrentFilter();
    if (strFilter != '') {
      let filter = JSON.parse(strFilter);

      this.form.controls.initialDate.setValue(filter.startDate);
      this.form.controls.finalDate.setValue(filter.endDate);
      this.form.controls.protocolNumber.setValue(filter.protocolNumber);

      this.selectedCategories = filter.categoryIds;
      this.selectedAttendants = filter.attendantIds;
      this.selectedClients = filter.clientIds;
      this.selectedStatus = filter.statusName;
    }
  }

  getCategories(name: string | null) {
    this.categoryFilter.pageNumber = 1;

    if (name) {
      this.categoryFilter.name = name;
      this.loadingSearchCategories = true;
    } else {
      this.loadingCategories = true;
      this.categoryFilter.name = null;
    }

    this.categoryService.get(this.categoryFilter).subscribe({
      next: (data) => {
        this.originalCategories = data.items;
        this.categories = data.items.map((category) =>
          category.client === null
            ? category.name
            : category.name + ' (' + category.client?.name + ') '
        );
        this.totalItemsCategories = data.totalItems;
        this.loadingCategories = false;
        this.loadingSearchCategories = false;
      },
      error: () => {
        this.toastrService.error('Não foi possível carregar as categorias.');
        this.loadingCategories = false;
        this.loadingSearchCategories = false;
      },
    });
  }

  getMoreCategories() {
    if (this.totalItemsCategories != this.categories.length) {
      this.loadingMoreCategories = true;

      if (this.categoryFilter.pageNumber) this.categoryFilter.pageNumber += 1;

      this.categoryService.get(this.categoryFilter).subscribe({
        next: (data) => {
          this.originalCategories = this.originalCategories.concat(data.items);
          this.totalItemsCategories = data.totalItems;

          this.categories = this.categories.concat(
            data.items.map((category) =>
              category.client === null
                ? category.name
                : category.name + ' (' + category.client?.name + ') '
            )
          );

          this.loadingMoreCategories = false;
        },
        error: () => {
          this.toastrService.error('Não foi possível carregar as categorias.');
          this.loadingMoreCategories = false;
        },
      });
    }
  }

  handleToggleAccordion() {
    this.isOpen = !this.isOpen;
  }

  clear() {
    this.form.reset();
    this.selectedCategories = [];
    this.selectedTypes = [];
    this.selectedStatus = [];
    this.selectedClients = [];
    this.selectedAttendants = [];
  }

  search() {
    let filter: GetTranscriptionsFilter = {} as GetTranscriptionsFilter;

    filter.protocolNumber = this.form.controls.protocolNumber.value ?? '';
    filter.startDate = this.form.controls.initialDate.value ?? '';
    filter.endDate = this.form.controls.finalDate.value ?? '';

    filter.attendantIds = this.getAttendantsIdFromText(
      this.form.controls.attendants.value || []
    );

    filter.clientIds = this.getClientsIdFromText(
      this.form.controls.clients.value || []
    );

    filter.categoryIds = this.getCategoriesIdFromName(
      this.form.controls.categories.value || []
    );

    filter.statusName = this.form.controls.status.value || [];
    filter.types = filter.types ?? [];
    this.storageService.setCurrentFilter(JSON.stringify(filter));
    this.filterEmmiter.emit(filter);

    const array: string[] = this.form.controls.types.value ?? [];
    if (array.includes('Áudio')) filter.types.push(0);
    if (array.includes('Chat')) filter.types.push(1);

    this.isOpen = false;
  }

  getStatusEnumFromFilter(filter: string[]): IncidentStatusEnum[] {
    const result: IncidentStatusEnum[] = [];

    filter.forEach((status) => {
      const statusEnumValue = Object.values(IncidentStatusEnum).find(
        (i) => i === status
      );
      if (statusEnumValue) result.push(statusEnumValue);
    });

    return result;
  }

  getCategoriesIdFromName(categories: string[]): string[] {
    let categoryIds: string[] = [];

    categories.forEach((c) => {
      let categoryName = c.split('(')[0].trim();
      let category = this.originalCategories.find(
        (c) => c.name.trim() === categoryName.trim()
      );
      if (category) categoryIds.push(category.id);
    });

    return categoryIds;
  }

  getAttendants(name: string | null) {
    this.attendantsFilter.pageNumber = 1;

    if (name) {
      this.attendantsFilter.name = name;
      this.loadingSearchAttendants = true;
    } else {
      this.loadingAttendants = true;
      this.attendantsFilter.name = null;
    }

    this.userService.get(this.attendantsFilter).subscribe({
      next: (data) => {
        this.originalAttendants = this.originalAttendants.concat(data.items);
        this.attendants = data.items.map(
          (attendant) => attendant.name + ' | ' + attendant.functional
        );
        this.totalItemsAttendants = data.totalItems;
        this.loadingAttendants = false;
        this.loadingSearchAttendants = false;
      },
      error: () => {
        this.toastrService.error('Não foi possível carregar os atendentes.');
        this.loadingAttendants = false;
        this.loadingSearchAttendants = false;
      },
    });
  }

  getMoreAttendants() {
    if (this.totalItemsAttendants != this.attendants.length) {
      this.loadingMoreAttendants = true;
      if (this.attendantsFilter.pageNumber)
        this.attendantsFilter.pageNumber += 1;

      this.userService.get(this.attendantsFilter).subscribe({
        next: (data) => {
          this.originalAttendants = this.originalAttendants.concat(data.items);
          this.attendants = this.attendants.concat(
            data.items.map(
              (attendant) => attendant.name + ' | ' + attendant.functional
            )
          );
          this.totalItemsAttendants = data.totalItems;
          this.loadingMoreAttendants = false;
        },
        error: () => {
          this.toastrService.error('Não foi possível carregar os atendentes.');
          this.loadingMoreAttendants = false;
        },
      });
    }
  }

  getClients(name: string | null) {
    this.clientsFilter.pageNumber = 1;

    if (name) {
      this.clientsFilter.name = name;
      this.loadingSearchClients = true;
    } else {
      this.loadingClients = true;
      this.clientsFilter.name = null;
    }

    this.clientService.get(this.clientsFilter).subscribe({
      next: (data) => {
        this.originalClients = data.items;
        this.clients = data.items.map((i) => i.name + ' | ' + i.cnpj);
        this.totalItemsClients = data.totalItems;
        this.loadingClients = false;
        this.loadingSearchClients = false;
      },
      error: () => {
        this.toastrService.error('Não foi possível carregar os clientes.');
        this.loadingClients = false;
        this.loadingSearchClients = false;
      },
    });
  }

  getMoreClients() {
    if (this.totalItemsClients != this.clients.length) {
      this.loadingMoreClients = true;
      if (this.clientsFilter.pageNumber) this.clientsFilter.pageNumber += 1;

      this.clientService.get(this.clientsFilter).subscribe({
        next: (data) => {
          this.originalClients = this.originalClients.concat(data.items);
          this.totalItemsClients = data.totalItems;
          this.clients = this.clients.concat(
            data.items.map((i) => i.name + ' | ' + i.cnpj)
          );
          this.loadingMoreClients = false;
        },
        error: () => {
          this.toastrService.error('Não foi possível carregar os clientes.');
          this.loadingMoreClients = false;
        },
      });
    }
  }

  getAttendantsIdFromText(users: string[]): string[] {
    if (!users || users.length === 0) return [];
    let usersId: string[] = [];
    users.forEach((name) => {
      let attendantName = name.split('|')[0].trim();
      let attendant = this.originalAttendants.find(
        (i) => i.name === attendantName
      );
      if (attendant) usersId.push(attendant.id);
    });

    return usersId;
  }

  getClientsIdFromText(clients: string[]): string[] {
    if (!clients || clients.length === 0) return [];
    let clientsId: string[] = [];
    clients.forEach((name) => {
      let clientName = name.split('|')[0].trim();
      let client = this.originalClients.find((i) => i.name === clientName);
      if (client) clientsId.push(client.id);
    });

    return clientsId;
  }
}
